import type { CSSProperties } from 'react';
import type { Country, Venue } from '@/types/domain';

interface PosterCardProps {
  posterImageSrc: string;
  venue: Venue;
  venueCountry: Country;
}

// Same 1080×1920 IG Stories canvas as ShareCard. The AI poster fills the
// whole frame; we layer a dark gradient + venue claim at the bottom 25%
// so the user's plan is legible regardless of poster brightness.
const W = 1080;
const H = 1920;
const BRAND_BOTTOM_OFFSET = 150;

const MONO: CSSProperties = { fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' };
const SERIF: CSSProperties = { fontFamily: '"Instrument Serif", Georgia, serif' };

// WCAG-ish helper duplicated from ShareCard to keep PosterCard self-contained.
function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const toLin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function lighten(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${toHex(lr)}${toHex(lg)}${toHex(lb)}`;
}

function accentForDark(primary: string): string {
  const L = relativeLuminance(primary);
  if (L < 0.08) return lighten(primary, 0.65);
  if (L < 0.2) return lighten(primary, 0.45);
  return lighten(primary, 0.25);
}

export function PosterCard({ posterImageSrc, venue, venueCountry }: PosterCardProps) {
  const accent = accentForDark(venueCountry.colors.primary);

  return (
    <div
      style={{
        width: W,
        height: H,
        position: 'relative',
        overflow: 'hidden',
        background: '#0a0a0a',
        fontFamily: '-apple-system, system-ui, sans-serif',
      }}
    >
      <img
        src={posterImageSrc}
        alt=""
        crossOrigin="anonymous"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Dark band with feathered top. Shorter than v1 so the poster keeps
          more real estate; the feather only needs to dissolve the seam, not
          provide acres of text room. */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 540,
          background:
            'linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,1) 18%, rgba(10,10,10,1) 100%)',
          pointerEvents: 'none',
        }}
        aria-hidden
      />

      {/* Flag-coded accent line at the seam — turns the band transition from
          "accidental black bar" into "intentional brand element". */}
      <div
        style={{
          position: 'absolute',
          bottom: 470,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(to right, ${venueCountry.colors.primary} 0%, ${venueCountry.colors.primary} 33%, ${venueCountry.colors.secondary} 33%, ${venueCountry.colors.secondary} 66%, ${venueCountry.colors.tertiary} 66%, ${venueCountry.colors.tertiary} 100%)`,
          pointerEvents: 'none',
        }}
        aria-hidden
      />

      <div
        style={{
          position: 'absolute',
          bottom: 290,
          left: 0,
          right: 0,
          padding: '0 50px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div
          style={{
            fontFamily: '-apple-system, "Helvetica Neue", system-ui, sans-serif',
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: 8,
            color: accent,
            textTransform: 'uppercase',
            marginBottom: 22,
          }}
        >
          I&apos;m going to
        </div>
        <div
          style={{
            fontFamily: '-apple-system, "Helvetica Neue", system-ui, sans-serif',
            fontSize: 64,
            fontWeight: 900,
            lineHeight: 1.02,
            color: '#fff',
            letterSpacing: -1,
            textTransform: 'uppercase',
          }}
        >
          {venue.name}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: BRAND_BOTTOM_OFFSET,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            ...MONO,
            fontSize: 44,
            fontWeight: 900,
            color: '#fff',
            letterSpacing: 14,
            textTransform: 'uppercase',
          }}
        >
          nynjwc
        </div>
        <div
          style={{
            ...SERIF,
            fontStyle: 'italic',
            fontSize: 26,
            color: '#c4c0bb',
            marginTop: 6,
            letterSpacing: 0.5,
          }}
        >
          find your spot
        </div>
      </div>
    </div>
  );
}
